import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { SdsForm, SmartSoftButton } from "soft_digi";
import { VENDER_RATE_URLS } from "../../api/UserUrls";
import { useSiteContext } from "../../contexts/SiteProvider";
import { changeDateTimeZoneFormat } from "../../services/core/CommonService";
import { showAlertAutoClose } from "../../services/notifyService";
import {
  company_address_all_select,
  hsn_all_select,
  hubs_get_all_select,
  vendors_get_all_select,
} from "../../services/site/SelectBoxServices";
import { post } from "../../services/smartApiService";
import VendorRatesSubForm from "./VendorRatesSubForm";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";

const schema = Yup.object({
  sd_hubs_id: Yup.object().required("Hub ID is required"),
  sd_customer_id: Yup.object().required("Company is required"),
  effective_date: Yup.date().required("Effective Date is required"),
  vendor_code: Yup.string().required("Customer Code is required"),
  cms_name: Yup.string().required("CMS Name is required"),
  bill_type: Yup.object().required("Billing Type is required"),
  rate_data: Yup.array()
    .of(
      Yup.object().shape({
        sd_hsn_id: Yup.object().required("HSN ID is required"),
        rate_type: Yup.object().required("Rate Type is required"),
        price: Yup.number().required("Price is required"),
      })
    )
    .min(1, "At least one rate entry is required"),
});

interface HeaderProps {
  loadTableData: () => void;
  dataIn: any;
}

const VendorRatesForms: React.FC<HeaderProps> = ({ loadTableData, dataIn }) => {
  const [allHubs, setAllHubs] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [custAddress, setCustAddress] = useState([]);
  const [allHsns, setAllHsn] = useState([]);
  const { closeModal } = useSiteContext();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: dataIn || { rate_data: [{}] }, 
    resolver: yupResolver(schema),
  });

  const customerIdWatch = useWatch({ control, name: "sd_customer_id" });

  useEffect(() => {
    hubs_get_all_select((data: any) => setAllHubs(data));
    vendors_get_all_select((data: any) => setAllVendors(data));
    hsn_all_select((data: any) => setAllHsn(data));
  }, []);

  useEffect(() => {
    const customer_id = customerIdWatch?.value;
    if (customer_id && customer_id > 0) {
      company_address_all_select(customer_id, (data: any) =>
        setCustAddress(data)
      );
    }
  }, [customerIdWatch?.value]);

  const onSubmit = (data: any) => {
    let url = VENDER_RATE_URLS.INSERT;
    if (data.ID !== undefined) {
      data["id"] = data.ID;
      url = VENDER_RATE_URLS.UPDATE;
    }

    const data_in = {
      ...data,
      rate_data: data?.rate_data || [],
      effective_date: changeDateTimeZoneFormat(
        data.effective_date,
        "YYYY-MM-DD"
      ),
    };

    const subscription = post(url, data_in).subscribe((response) => {
      loadTableData();
      showAlertAutoClose("Data Saved Successfully", "success");
      closeModal();
    });

    return () => {
      subscription.unsubscribe();
    };
  };

  const formElements: SdsFormElementProps[] = [
    {
      type: "SELECT_BOX",
      width: 6,
      name: "sd_hubs_id",
      element: {
        label: "Hub ID",
        isRequired: true,
        options: allHubs,
        inputType: "BORDER_LABEL",
        disabled: dataIn?.ID ? true : false,
      },
    },
    {
      type: "SELECT_BOX",
      width: 6,
      name: "sd_customer_id",
      element: {
        label: "Company",
        isRequired: true,
        options: allVendors,
        inputType: "BORDER_LABEL",
        disabled: dataIn?.ID ? true : false,
      },
    },
    {
      type: "SELECT_BOX",
      width: 6,
      name: "sd_customer_address_id",
      element: {
        label: "Billing Address",
        isRequired: true,
        options: custAddress,
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "DATE",
      width: 6,
      name: "effective_date",
      element: {
        label: "Effective Date",
        placeHolder: "DD-MM-YYYY",
        isRequired: true,
        inputType: "BORDER_LABEL",
        inputProps: { isFocussed: true },
      },
    },
    {
      type: "TEXT_BOX",
      width: 6,
      name: "vendor_code",
      element: {
        label: "Customer Code",
        isRequired: true,
        inputType: "BORDER_LABEL",
        inputProps: { isFocussed: true },
      },
    },
    {
      type: "TEXT_BOX",
      width: 6,
      name: "cms_name",
      element: {
        label: "CMS Name",
        isRequired: true,
        inputType: "BORDER_LABEL",
        inputProps: { isFocussed: true },
      },
    },
    {
      type: "SELECT_BOX",
      width: 6,
      name: "bill_type",
      element: {
        label: "Select Unit Billing Type",
        isRequired: true,
        options: [
          { value: "CMS", label: "CMS" },
          { value: "SUB_METER", label: "SUB_METER" },
        ],
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "LABEL",
      name: "label_date",
      width: 12,
      labelComponent: (
        <VendorRatesSubForm
          errors={errors}
          control={control}
          watch={watch}
          setValue={setValue}
        />
      ),
    },
  ];

  return (
    <div className="sd-efl-input">
      <SdsForm elements={formElements} errors={errors} control={control} />
      <div className="has-text-right">
        <SmartSoftButton
          label="Cancel"
          classList={["button", "mt-4", "mr-4", "smart-third-button"]}
          onClick={closeModal}
        />
        <SmartSoftButton
          label={dataIn?.ID ? "Update" : "Submit"}
          rightIcon="fa fa-arrow-right"
          classList={["button", "mt-4", "smart-action-button"]}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </div>
  );
};

export default VendorRatesForms;